import Button from 'components/ui/Button';
import { Camera, Loader, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useDeletePhoto } from '../hooks/useDeletePhoto';
import { useUploadPhoto } from '../hooks/useUploadPhoto';
import { useUserProfile } from '../hooks/useUserProfile';

const ProfilePhotoUpload = () => {
  const { mutate: uploadPhoto, isPending: isUploading } = useUploadPhoto();
  const { mutate: deletePhoto, isPending: isDeleting } = useDeletePhoto();
  const { data } = useUserProfile();
  const user = data?.data;
  const fileInputRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const validateFile = (file) => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return false;
    }

    return true;
  };

  const handleFileSelect = (file) => {
    if (!file || !validateFile(file)) return;

    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileSelect(file);
  };

  const handleUpload = () => {
    if (!selectedFile || !user?._id) return;

    uploadPhoto(
      { file: selectedFile, adminId: user._id },
      {
        onSuccess: () => {
          handleCancel();
        },
      }
    );
  };

  const handleDelete = () => {
    if (!user?._id) return;

    if (window.confirm('Are you sure you want to delete your profile photo?')) {
      deletePhoto(user._id);
    }
  };

  const handleCancel = () => {
    setPreview(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getInitials = () => {
    const name = user?.name || 'User';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isLoading = isUploading || isDeleting;

  return (
    <div className='space-y-6'>
      {/* Current Photo Section */}
      <div>
        <h3 className='text-lg font-semibold text-foreground mb-4'>Current Profile Photo</h3>
        <div className='flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6'>
          <div className='relative'>
            {user?.profilePhoto ? (
              <img
                src={user.profilePhoto}
                alt='Profile'
                className='w-32 h-32 rounded-full object-cover border-4 border-border'
              />
            ) : (
              <div className='w-32 h-32 rounded-full bg-primary flex items-center justify-center text-white text-3xl font-semibold border-4 border-border'>
                {getInitials()}
              </div>
            )}
          </div>

          {user?.profilePhoto && (
            <div className='flex flex-col justify-center space-y-2'>
              <p className='text-sm text-muted-foreground'>You can update or delete your profile photo</p>
              <Button
                variant='outline'
                onClick={handleDelete}
                disabled={isLoading}
                className='flex items-center space-x-2 text-destructive hover:text-destructive hover:bg-destructive/10'
              >
                {isDeleting ? (
                  <>
                    <Loader size={16} className='animate-spin' />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    <span>Delete Photo</span>
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Upload New Photo Section */}
      <div className='pt-6 border-t border-border'>
        <h3 className='text-lg font-semibold text-foreground mb-4'>Upload New Photo</h3>

        {/* Preview Section */}
        {preview && (
          <div className='mb-6'>
            <p className='text-sm font-medium text-foreground mb-3'>Preview</p>
            <div className='flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6'>
              <div className='relative'>
                <img
                  src={preview}
                  alt='Preview'
                  className='w-32 h-32 rounded-full object-cover border-4 border-primary'
                />
              </div>
              <div className='flex flex-col justify-center space-y-3'>
                <p className='text-sm text-muted-foreground'>
                  Ready to upload: <span className='font-medium text-foreground'>{selectedFile?.name}</span>
                </p>
                <div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3'>
                  <Button
                    onClick={handleUpload}
                    disabled={isLoading}
                    className='flex items-center justify-center space-x-2'
                  >
                    {isUploading ? (
                      <>
                        <Loader size={16} className='animate-spin' />
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload size={16} />
                        <span>Upload Photo</span>
                      </>
                    )}
                  </Button>
                  <Button variant='outline' onClick={handleCancel} disabled={isLoading}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Upload Area */}
        {!preview && (
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type='file'
              accept='image/*'
              onChange={handleFileInputChange}
              className='hidden'
            />

            <div className='flex flex-col items-center space-y-4'>
              <div className='w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center'>
                <Camera size={32} className='text-primary' />
              </div>

              <div className='space-y-2'>
                <p className='text-base font-medium text-foreground'>
                  {isDragging ? 'Drop your image here' : 'Upload a profile photo'}
                </p>
                <p className='text-sm text-muted-foreground'>Drag and drop or click to browse</p>
              </div>

              <Button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                className='flex items-center space-x-2'
              >
                <Upload size={16} />
                <span>Choose File</span>
              </Button>

              <div className='text-xs text-muted-foreground space-y-1'>
                <p>• Supported formats: JPG, PNG, GIF, WebP</p>
                <p>• Maximum file size: 5MB</p>
                <p>• Recommended: Square image, at least 400x400px</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className='pt-6 border-t border-border'>
        <h4 className='text-sm font-semibold text-foreground mb-3'>Photo Tips</h4>
        <ul className='space-y-2 text-sm text-muted-foreground'>
          <li className='flex items-start space-x-2'>
            <span className='text-primary mt-0.5'>•</span>
            <span>Use a clear, well-lit photo of your face</span>
          </li>
          <li className='flex items-start space-x-2'>
            <span className='text-primary mt-0.5'>•</span>
            <span>Square images work best (1:1 aspect ratio)</span>
          </li>
          <li className='flex items-start space-x-2'>
            <span className='text-primary mt-0.5'>•</span>
            <span>Avoid photos with busy backgrounds</span>
          </li>
          <li className='flex items-start space-x-2'>
            <span className='text-primary mt-0.5'>•</span>
            <span>Professional headshots work great</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
